import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2, ShieldCheck } from "lucide-react";
import { PageHero, Eyebrow } from "@/components/site/ui";
import { Reveal } from "@/components/site/motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { submitContact } from "@/lib/api";
import { CONTACT, PILLARS } from "@/data/content";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", pillar: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de renseigner nom, email et message.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact(form);
      if (res.email_sent) {
        toast.success("Message envoyé ! Nous vous répondrons rapidement.");
      } else {
        toast.success("Message bien reçu. Nous vous recontactons rapidement.");
      }
      setForm({ name: "", email: "", phone: "", pillar: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue. Réessayez ou écrivez-nous directement.");
    } finally {
      setLoading(false);
    }
  };

  const coords = [
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Phone, label: "Téléphone", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
    { icon: MapPin, label: "Adresse", value: CONTACT.address },
    { icon: MapPin, label: "Localisations", value: CONTACT.locations },
  ];

  return (
    <>
      <PageHero
        eyebrow="Contact"
        titleLines={["Donnons vie", "à votre projet"]}
        subtitle="Première consultation gratuite et sans engagement. Parlons de vos ambitions."
      />

      <section className="bg-cream py-24 sm:py-32" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-5 gap-14">
          <Reveal className="lg:col-span-2">
            <Eyebrow>Coordonnées</Eyebrow>
            <h2 className="mt-5 font-display text-3xl font-medium text-navy">Restons en <em className="not-italic italic text-gold-gradient">contact</em></h2>
            <div className="mt-8 space-y-6">
              {coords.map((c) => (
                <div key={c.label} className="flex items-start gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-gold shrink-0">
                    <c.icon size={19} />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-anthracite/60">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="font-body text-navy hover:text-gold transition-colors">{c.value}</a>
                    ) : (
                      <p className="font-body text-navy">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-3xl border border-navy/10 bg-white p-8 sm:p-10" data-testid="contact-form">
              <h3 className="font-display text-2xl text-navy">Consultation gratuite</h3>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <div>
                  <Label htmlFor="name" className="text-navy">Nom complet *</Label>
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Votre nom" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-navy">Email *</Label>
                  <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} placeholder="vous@email.com" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-navy">Téléphone</Label>
                  <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="+33 …" className="mt-2" />
                </div>
                <div>
                  <Label className="text-navy">Pôle d'intérêt</Label>
                  <Select value={form.pillar} onValueChange={set("pillar")}>
                    <SelectTrigger data-testid="contact-pillar" className="mt-2">
                      <SelectValue placeholder="Choisir un pôle" />
                    </SelectTrigger>
                    <SelectContent>
                      {PILLARS.map((p) => (
                        <SelectItem key={p.key} value={p.key}>{p.title}</SelectItem>
                      ))}
                      <SelectItem value="autre">Autre raison</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-5">
                <Label htmlFor="message" className="text-navy">Message *</Label>
                <Textarea id="message" data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Décrivez votre projet…" rows={5} className="mt-2" />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="mt-6 group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-7 py-4 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <>Envoyer le message <Send size={16} /></>}
              </button>
              <p className="mt-4 flex items-center gap-2 text-xs text-anthracite/60 font-body">
                <ShieldCheck size={14} className="text-gold" /> Vos informations sont traitées en toute confidentialité et ne seront jamais partagées.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
